/**
 * Transparency types for the chat reasoning trace UI.
 *
 * Ported from Keystone's transparency contract. These schemas describe
 * provenance steps emitted by the pipeline — what data was accessed,
 * what tools were called, and what LLM generations occurred.
 *
 * Zod schemas are canonical; types are derived via `z.infer`/`z.enum`
 * (`terra/no-parallel-schema-and-type`). Every step variant uses
 * `z.looseObject` (passthrough) because the wire carries fields these
 * schemas don't model (see bead terra-design-v2-chat-yub.14) — a `.strict()`
 * object would drop live traffic.
 */
import { z } from 'zod';

// Source kind — where retrieved data came from.

export const SourceKindSchema = z.enum([
  'salesforce', 'gong', 'database', 'blob', 'file',
  'gmail', 'notion', 'google_drive', 'slack',
]);
/** Where data was retrieved from. */
export type SourceKind = z.infer<typeof SourceKindSchema>;

/** Frontend visibility tier: 1 = always, 2 = one click, 3 = on demand. */
const TierSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

/** A labeled evidence item, optionally deep-linked to a source record. */
const EvidenceItemSchema = z.object({
  /** Human-readable label. E.g. "Sarah Chen — VP Engineering" */
  label: z.string(),
  /** Deep-link URL to the source record. E.g. Salesforce record URL, Gong call URL. */
  url: z.string().optional(),
});

// Base fields shared by every step

/** Fields present on every transparency step. */
const baseStep = {
  /** Human-readable label (e.g. "Fetch Salesforce contacts"). */
  name: z.string(),
  /** Milliseconds since the request started (Date.now() - requestStart). */
  startMs: z.number(),
  /** Wall-clock duration of this step in milliseconds. */
  durationMs: z.number(),
  /** Controls frontend visibility: 1 = always, 2 = one click, 3 = on demand. */
  tier: TierSchema,
  /** What this step did, in plain language. */
  narrativeAction: z.string().optional(),
  /** What was found/concluded. */
  narrativeResult: z.string().optional(),
  /** Specific items from the source that support this finding. */
  evidenceItems: z.array(EvidenceItemSchema).optional(),
};

// Step variants

/** A data retrieval from an external or internal source. */
const RetrievalStepSchema = z.looseObject({
  type: z.literal('retrieval'),
  ...baseStep,
  /** Where the data came from. */
  source: z.object({
    kind: SourceKindSchema,
    /** Identifier for the source (e.g. table name, SOQL object, file path). */
    identifier: z.string(),
    /** The query or filter used, if applicable. */
    query: z.string().optional(),
    /** ISO-8601 timestamp or human description of data freshness. */
    freshness: z.string().optional(),
    /** URL to the original record in the source system. */
    url: z.string().optional(),
  }),
  /** Number of records / rows / items returned (omit when unknown). */
  resultCount: z.number().optional(),
  /** Optional short preview of the results for tier-2/3 display. */
  resultPreview: z.string().optional(),
  /** Individual data item labels for scrollable list display. */
  resultItems: z.array(EvidenceItemSchema).optional(),
});

/** An explicit tool call made by the agent. */
export const ToolCallStepSchema = z.looseObject({
  type: z.literal('tool'),
  ...baseStep,
  /**
   * Which tool was invoked. Free-form: skill runs surface arbitrary model-chosen
   * tool names (compose_form_field, search_artifacts, ...), not a closed set.
   */
  toolName: z.string(),
  /** The arguments passed to the tool (e.g. SQL query, bash command). */
  arguments: z.string(),
  /** Summary or raw result from the tool, if captured. */
  result: z.string().optional(),
  /** Number of rows returned (for SQL-like tools). */
  resultRows: z.number().optional(),
  /**
   * Lifecycle status. Producers (e.g. SkillStreamRuntimeProvider's
   * `toolCallsToSteps`) set this from the upstream ToolCall.status so
   * `ChatReasoningTrace`'s ToolChip can render the correct indicator
   * (✓ / ✗ / pulse) without inferring from `durationMs`.
   */
  status: z.enum(['running', 'success', 'error']).optional(),
});

/** An LLM generation (chat completion). */
export const GenerationStepSchema = z.looseObject({
  type: z.literal('generation'),
  ...baseStep,
  /** Model identifier (e.g. "claude-sonnet-4-6"). */
  model: z.string(),
  /** What this generation was for (e.g. "synthesis", "entity resolution"). */
  purpose: z.string(),
  /** Tokens consumed by the prompt. */
  inputTokens: z.number(),
  /** Tokens produced by the model. */
  outputTokens: z.number(),
  /** System prompt text, if captured (tier 3 only). */
  systemPrompt: z.string().optional(),
  /** LLM reasoning text from this turn (what it decided to do and why). */
  reasoning: z.string().optional(),
});

/** A data transformation (filter, merge, format, etc.). */
const TransformStepSchema = z.looseObject({
  type: z.literal('transform'),
  ...baseStep,
  /** What operation was performed (e.g. "merge contacts", "deduplicate"). */
  operation: z.string(),
  /** Description of the input data shape / count. */
  inputDescription: z.string(),
  /** Description of the output data shape / count. */
  outputDescription: z.string(),
});

// Discriminated union

export const TransparencyStepSchema = z.discriminatedUnion('type', [
  RetrievalStepSchema,
  ToolCallStepSchema,
  GenerationStepSchema,
  TransformStepSchema,
]);

export type TransparencyStep = z.infer<typeof TransparencyStepSchema>;
export type ToolCallStep = z.infer<typeof ToolCallStepSchema>;
export type GenerationStep = z.infer<typeof GenerationStepSchema>;
